import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';

type AppMode = 'crm' | 'lis' | 'billing' | 'vob' | 'integrations' | 'tools';

interface AppModeContextType {
  mode: AppMode;
  setMode: (mode: AppMode) => void;
  isCRMMode: boolean;
  isLISMode: boolean;
  isBillingMode: boolean;
  isVOBMode: boolean;
  isIntegrationsMode: boolean;
  isToolsMode: boolean;
  toggleMode: () => void;
}

const AppModeContext = createContext<AppModeContextType | undefined>(undefined);

export const AppModeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [mode, setModeState] = useState<AppMode>(() => {
    // Load from localStorage or default to CRM
    const saved = localStorage.getItem('appMode');
    return (saved === 'lis' || saved === 'crm' || saved === 'billing' || saved === 'vob' || saved === 'integrations' || saved === 'tools') ? saved : 'crm';
  });

  // Persist mode to localStorage
  useEffect(() => {
    localStorage.setItem('appMode', mode);
  }, [mode]);

  const setMode = useCallback((newMode: AppMode) => {
    setModeState(newMode);
  }, []);

  const toggleMode = useCallback(() => {
    setModeState(current => {
      if (current === 'crm') return 'lis';
      if (current === 'lis') return 'billing';
      if (current === 'billing') return 'vob';
      if (current === 'vob') return 'integrations';
      if (current === 'integrations') return 'tools';
      return 'crm';
    });
  }, []);

  const value = useMemo(() => ({
    mode,
    setMode,
    isCRMMode: mode === 'crm',
    isLISMode: mode === 'lis',
    isBillingMode: mode === 'billing',
    isVOBMode: mode === 'vob',
    isIntegrationsMode: mode === 'integrations',
    isToolsMode: mode === 'tools',
    toggleMode
  }), [mode, setMode, toggleMode]);

  return (
    <AppModeContext.Provider value={value}>
      {children}
    </AppModeContext.Provider>
  );
};


export const useAppMode = () => {
  const context = useContext(AppModeContext);
  if (context === undefined) {
    throw new Error('useAppMode must be used within an AppModeProvider');
  }
  return context;
};

export default AppModeContext;

